import React, { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Loader2 } from 'lucide-react';
import { blobToFile, getCroppedImageBlob } from '../utils/getCroppedImage';

/** Wide strip for ID card footer banner (~200×38px visual). */
export const ID_CARD_FOOTER_ASPECT = 5;

export default function ImageCropDialog({
  open,
  onOpenChange,
  imageSrc,
  title = 'Crop image',
  description = 'Drag to reposition. Use the slider to zoom.',
  aspect = ID_CARD_FOOTER_ASPECT,
  outputFileName = 'id-card-footer.jpg',
  onConfirm,
  confirming = false,
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels || !onConfirm) return;
    setProcessing(true);
    try {
      const usePng = outputFileName.toLowerCase().endsWith('.png');
      const blob = await getCroppedImageBlob(
        imageSrc,
        croppedAreaPixels,
        usePng ? 'image/png' : 'image/jpeg',
        usePng ? undefined : 0.92
      );
      const file = blobToFile(blob, outputFileName);
      await onConfirm(file);
    } finally {
      setProcessing(false);
    }
  };

  const busy = processing || confirming;

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {imageSrc ? (
          <div className="space-y-4">
            <div className="relative w-full h-[min(50vh,280px)] bg-muted rounded-lg overflow-hidden">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                objectFit="horizontal-cover"
              />
            </div>
            <div className="space-y-2 px-1">
              <p className="text-xs text-muted-foreground">Zoom</p>
              <Slider
                min={1}
                max={3}
                step={0.05}
                value={[zoom]}
                onValueChange={([z]) => setZoom(z ?? 1)}
              />
            </div>
          </div>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={busy || !imageSrc || !croppedAreaPixels}
            onClick={handleConfirm}
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading…
              </>
            ) : (
              'Crop & upload'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
